import { AppContext } from "./AppContext";
import { useState, ReactNode, useMemo, useEffect, useCallback } from "react";
import { useStorage } from "hooks/useStorage";
import { ModalsType } from "../modal-context/ModalContext";
import { LatestTradeType, TokenType } from "types/token.type";
import { userType } from "types/user.type";
import { fetchCurrentPepePrice } from "utils/axios";
import { normalizeLatestTrade, normalizeToken } from "utils/helpers";

interface AppProviderProps {
  children: ReactNode;
}

const AppProvider = ({ children }: AppProviderProps) => {
  const [user, setUser] = useState<userType>({} as userType);
  const [latestTradeRaw, setLatestTradeRaw] = useState<LatestTradeType | null>(null);
  const [latestTrade, setLatestTradeState] = useState<LatestTradeType | null>(null);
  const [rawTokens, setRawTokens] = useState<TokenType[]>([]);
  const [tokens, setTokensState] = useState<TokenType[]>([]);
  const [modalStatus, setModalStatus] = useState(false);
  const [activeModal, setActiveModal] = useState<ModalsType>("edit-profile");
  const [showAnimations, setShowAnimations] = useStorage<boolean>(
    "showAnimations",
    false
  );
  const [selectWalletModalState, setSelectWalletModalState] = useState<boolean>(false);
  const [slippage, setSlippage] = useStorage<string>("slippage", "2");
  const [pepeTokenPrice, setPepeTokenPrice] = useState<number | null>(null);
  const [pepePriceLoading, setPepePriceLoading] = useState<boolean>(true);
  const [watchlistTokens, setWatchlistTokens] = useState<string[]>([]);

  const deriveTokens = useCallback((list: TokenType[], ethPriceUsd: number) => {
    if (!Array.isArray(list) || list.length === 0) {
      return [];
    }
    return list.map((token) => normalizeToken(token, ethPriceUsd));
  }, []);

  const deriveLatestTrade = useCallback(
    (trade: LatestTradeType | null, ethPriceUsd: number) => {
      if (!trade) {
        return null;
      }
      return normalizeLatestTrade(trade, { ethPriceUsd });
    },
    []
  );

  const setTokens = useCallback(
    (nextTokens: TokenType[]) => {
      setRawTokens(nextTokens);
      const price = typeof pepeTokenPrice === "number" ? pepeTokenPrice : 0;
      setTokensState(deriveTokens(nextTokens, price));
    },
    [deriveTokens, pepeTokenPrice]
  );

  const setLatestTrade = useCallback(
    (trade: LatestTradeType | null) => {
      setLatestTradeRaw(trade);
      const price = typeof pepeTokenPrice === "number" ? pepeTokenPrice : 0;
      setLatestTradeState(deriveLatestTrade(trade, price));
    },
    [deriveLatestTrade, pepeTokenPrice]
  );

  // Initialize PEPE token price from backend
  useEffect(() => {
    const initializePepePrice = async () => {
      try {
        console.log("🔄 Initializing PEPE token price from backend...");
        setPepePriceLoading(true);
        
        // Try to fetch price with retries
        let price = null;
        let retries = 0;
        const maxRetries = 5; // Increased retries since backend might need time to populate database
        
        while (!price && retries < maxRetries) {
          price = await fetchCurrentPepePrice();
          if (!price) {
            retries++;
            console.log(`⚠️ Price fetch attempt ${retries} failed, retrying in 3 seconds...`);
            await new Promise(resolve => setTimeout(resolve, 3000)); // Increased delay
          }
        }
        
        if (price) {
          console.log("✅ PEPE token price initialized:", price);
          setPepeTokenPrice(price);
        } else {
          console.log("❌ Failed to initialize PEPE token price after retries");
          // Don't set to 0, keep as null to indicate no price available
          setPepeTokenPrice(null);
        }
      } catch (error) {
        console.error("❌ Error initializing PEPE token price:", error);
        setPepeTokenPrice(null); // Keep as null on error
      } finally {
        setPepePriceLoading(false);
      }
    };

    initializePepePrice();
  }, []);

  useEffect(() => {
    const priceInterval = setInterval(async () => {
      try {
        const latestPrice = await fetchCurrentPepePrice();
        if (typeof latestPrice === "number" && Number.isFinite(latestPrice)) {
          setPepeTokenPrice(latestPrice);
        }
      } catch (error) {
        console.error("Failed to refresh PEPE price:", error);
      }
    }, 10000);

    return () => {
      clearInterval(priceInterval);
    };
  }, []);
  useEffect(() => {
    const price = typeof pepeTokenPrice === "number" ? pepeTokenPrice : 0;
    if (rawTokens.length > 0) {
      setTokensState(deriveTokens(rawTokens, price));
    }
    if (latestTradeRaw) {
      setLatestTradeState(deriveLatestTrade(latestTradeRaw, price));
    }
  }, [pepeTokenPrice, rawTokens, latestTradeRaw, deriveTokens, deriveLatestTrade]);

  const effectivePepeTokenPrice = typeof pepeTokenPrice === "number" ? pepeTokenPrice : 0;

  const value = useMemo(() => ({
    user,
    setUser,
    latestTrade,
    setLatestTrade,
    tokens,
    setTokens,
    showAnimations,
    setShowAnimations,
    selectWalletModalState,
    setSelectWalletModalState,  
    modalStatus,
    activeModal,
    setModalStatus,
    setActiveModal,
    slippage,
    setSlippage,
    pepeTokenPrice: effectivePepeTokenPrice,
    setPepeTokenPrice,
    pepePriceLoading,
    watchlistTokens,
    setWatchlistTokens,
  }), [
    user,
    latestTrade,
    setLatestTrade,
    tokens,
    setTokens,
    showAnimations,
    modalStatus,
    activeModal,
    slippage,
    selectWalletModalState,
    effectivePepeTokenPrice,
    pepePriceLoading,
    watchlistTokens,
  ]);

  return (
    <AppContext.Provider
      value={value}
    >
      {children}
    </AppContext.Provider>
  );
};

export default AppProvider;
